import React, { forwardRef } from 'react';
import { ChevronDown, Download, Search } from 'lucide-react';
import { PERFORMANCE_TABS, RETURN_FILTERS } from '../../utils/ipoPerformance';

const CHIP_TONES = {
  all: { idle: 'bg-slate-100 text-slate-700 hover:bg-slate-200/70', active: 'bg-[#008080] text-white shadow-sm' },
  multibagger: { idle: 'bg-slate-100 text-slate-700 hover:bg-slate-200/70', active: 'bg-[#008080] text-white shadow-sm' },
  profit: { idle: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', active: 'bg-emerald-600 text-white shadow-sm' },
  discount: { idle: 'bg-rose-50 text-rose-600 hover:bg-rose-100', active: 'bg-rose-600 text-white shadow-sm' },
};

// Board tabs, listing year, export, return filters and search for the performance table
export const PerformanceControls = forwardRef(
  (
    { tab, onTabChange, tabCounts, year, years, onYearChange, onExport, canExport, filter, onFilterChange, allCount, query, onQueryChange },
    ref
  ) => (
    <div ref={ref} className="scroll-mt-20 bg-white rounded-2xl border border-slate-200/90 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 sm:p-4">
        <div role="group" aria-label="Board" className="inline-flex self-start gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80 max-w-full overflow-x-auto">
          {PERFORMANCE_TABS.map((option) => {
            const active = option.key === tab;
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => onTabChange(option.key)}
                className={`px-3.5 sm:px-5 py-2 rounded-lg text-xs sm:text-[13px] font-bold whitespace-nowrap transition-all ${
                  active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {option.label} ({tabCounts[option.key]})
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="performance-year" className="text-xs font-semibold text-slate-600">
            Listing Year:
          </label>
          <span className="relative">
            <select
              id="performance-year"
              value={year}
              onChange={(e) => onYearChange(Number(e.target.value))}
              className="appearance-none w-28 sm:w-44 pl-3 pr-8 py-2 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-800 cursor-pointer hover:border-slate-300 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
            >
              {years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </span>
          <button
            type="button"
            onClick={onExport}
            disabled={!canExport}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-3 sm:px-4 py-3 border-t border-slate-100">
        <div role="group" aria-label="Filter by return" className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500 mr-1">Filter by:</span>
          {RETURN_FILTERS.map((option) => {
            const active = option.key === filter;
            const tones = CHIP_TONES[option.key];
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => onFilterChange(option.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-bold whitespace-nowrap transition-colors ${
                  active ? tones.active : tones.idle
                }`}
              >
                {option.label}
                {option.key === 'all' && ` (${allCount})`}
              </button>
            );
          })}
        </div>

        <label className="relative block lg:w-72">
          <span className="sr-only">Search company or ticker</span>
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search company or ticker..."
            className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
          />
        </label>
      </div>
    </div>
  )
);

PerformanceControls.displayName = 'PerformanceControls';

export default PerformanceControls;
